import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend,
} from "recharts";
import {
  Heartbeat,
  WifiHigh,
  WifiSlash,
  ShieldWarning,
  Warning,
  Spinner,
  Lightning,
  Circuitry,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DeviceHealthPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);
  const isAdmin = user?.role === "admin";

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = isAdmin ? "/api/admin/health-summary" : "/api/customer/health-summary";
      const res = await axios.get(`${API_URL}${endpoint}?days=${period}`);
      setData(res.data);
    } catch {
      toast.error(t("health.report_error"));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, period, t]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  const avgScore = data.device_health?.length
    ? Math.round(data.device_health.reduce((s, d) => s + d.score, 0) / data.device_health.length)
    : 100;

  const scoreColor = avgScore >= 80 ? "text-emerald-600" : avgScore >= 50 ? "text-amber-600" : "text-red-600";
  const scoreBg = avgScore >= 80 ? "bg-emerald-50 border-emerald-200" : avgScore >= 50 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";

  const trendData = (data.daily_trend || []).map(d => ({
    ...d,
    date: d.date?.slice(5) || "",
  }));

  return (
    <div className="space-y-6" data-testid="device-health-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("health.title")}</h1>
          <p className="text-slate-500 mt-1">{t("health.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2" data-testid="period-selector">
          {[7, 14, 30].map(d => (
            <Button key={d} size="sm"
              variant={period === d ? "default" : "outline"}
              className={period === d ? "bg-indigo-600 hover:bg-indigo-700" : ""}
              onClick={() => setPeriod(d)}
              data-testid={`period-${d}`}>
              {d} {t("health.days")}
            </Button>
          ))}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className={`border ${scoreBg}`}>
          <CardContent className="p-4 text-center">
            <Heartbeat size={24} className={`mx-auto ${scoreColor} mb-1`} />
            <p className={`text-2xl font-bold ${scoreColor}`} data-testid="avg-score">{avgScore}</p>
            <p className="text-xs text-slate-600">{t("health.avg_score")}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <Circuitry size={24} className="mx-auto text-indigo-600 mb-1" />
            <p className="text-2xl font-bold text-slate-800" data-testid="total-devices">{data.total_devices}</p>
            <p className="text-xs text-slate-600">{t("dashboard.total_devices")}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4 text-center">
            <WifiHigh size={24} className="mx-auto text-emerald-600 mb-1" />
            <p className="text-2xl font-bold text-emerald-700" data-testid="online-count">{data.online_count}</p>
            <p className="text-xs text-emerald-600">Online</p>
          </CardContent>
        </Card>
        <Card className="border-slate-300 bg-slate-100/50">
          <CardContent className="p-4 text-center">
            <WifiSlash size={24} className="mx-auto text-slate-500 mb-1" />
            <p className="text-2xl font-bold text-slate-600" data-testid="offline-count">{data.offline_count}</p>
            <p className="text-xs text-slate-500">Offline</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4 text-center">
            <ShieldWarning size={24} className="mx-auto text-red-600 mb-1" />
            <p className="text-2xl font-bold text-red-700" data-testid="total-alerts">{data.total_alerts}</p>
            <p className="text-xs text-red-600">{t("health.total_alarm")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trend Chart */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t("health.daily_trend")}</CardTitle>
          </CardHeader>
          <CardContent>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260} minWidth={0}>
                <BarChart data={trendData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    labelFormatter={(v) => `${t("health.date")}: ${v}`}
                  />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: "12px" }} />
                  <Bar dataKey="critical" name={t("common.critical")} fill="#ef4444" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="warning" name={t("common.warning")} fill="#f59e0b" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="info" name={t("common.info")} fill="#3b82f6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">
                {t("health.no_data")}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alarm Type Distribution */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t("health.type_dist")}</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(data.type_counts || {}).length > 0 ? (
              <div className="space-y-3 py-2">
                {Object.entries(data.type_counts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => {
                    const maxCount = Math.max(...Object.values(data.type_counts));
                    const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                    const typeLabels = {
                      bypass_confirmed: t("alert_types.bypass_confirmed"),
                      bypass_warning: t("alert_types.bypass_warning"),
                      offline: t("alert_types.offline"),
                      online: t("alert_types.online"),
                      leakage_current: t("alert_types.leakage_current"),
                      fault: t("alert_types.fault"),
                      error: t("alert_types.error"),
                    };
                    const colors = {
                      bypass_confirmed: "bg-red-500",
                      bypass_warning: "bg-orange-400",
                      offline: "bg-slate-400",
                      online: "bg-emerald-400",
                      leakage_current: "bg-purple-500",
                      fault: "bg-red-400",
                      error: "bg-amber-400",
                    };
                    return (
                      <div key={type} data-testid={`type-bar-${type}`}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-700 font-medium">{typeLabels[type] || type}</span>
                          <span className="text-slate-500">{count}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2.5">
                          <div className={`h-2.5 rounded-full transition-all ${colors[type] || "bg-indigo-400"}`}
                            style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">
                {t("health.no_alert_records")}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Problematic Devices */}
      {data.top_problematic_devices?.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Warning size={20} className="text-amber-600" />
              {t("health.problem_devices")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="problematic-devices-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 text-slate-600 font-medium">{t("devices.meter")}</th>
                    <th className="text-center py-2 px-3 text-slate-600 font-medium">{t("health.total_alarm")}</th>
                    <th className="text-center py-2 px-3 text-slate-600 font-medium">{t("common.critical")}</th>
                    <th className="text-center py-2 px-3 text-slate-600 font-medium">{t("common.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_problematic_devices.map((d, idx) => (
                    <tr key={d.device_id} className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`problem-device-${idx}`}>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <Lightning size={16} className="text-amber-500" />
                          <span className="font-medium text-slate-800">{d.device_name || d.device_id}</span>
                        </div>
                      </td>
                      <td className="text-center py-2.5 px-3">
                        <Badge variant="outline">{d.total}</Badge>
                      </td>
                      <td className="text-center py-2.5 px-3">
                        {d.critical > 0 ? (
                          <Badge className="bg-red-100 text-red-700">{d.critical}</Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </td>
                      <td className="text-center py-2.5 px-3">
                        <Badge className={d.critical > 3 ? "bg-red-100 text-red-700" : d.total > 5 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>
                          {d.critical > 3 ? t("common.critical") : d.total > 5 ? t("health.attention") : "Normal"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Device Health Scores */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Heartbeat size={20} className="text-indigo-600" />
            {t("health.device_scores")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.device_health?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.device_health.map((d) => {
                const sc = d.score;
                const bg = sc >= 80 ? "border-emerald-200 bg-emerald-50/30" : sc >= 50 ? "border-amber-200 bg-amber-50/30" : "border-red-200 bg-red-50/30";
                const textColor = sc >= 80 ? "text-emerald-700" : sc >= 50 ? "text-amber-700" : "text-red-700";
                return (
                  <div key={d.device_id} className={`p-3 rounded-xl border ${bg} flex items-center justify-between`}
                    data-testid={`health-card-${d.device_id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sc >= 80 ? "bg-emerald-100" : sc >= 50 ? "bg-amber-100" : "bg-red-100"}`}>
                        <span className={`text-lg font-bold ${textColor}`}>{sc}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{d.device_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {d.online ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0">{t("common.online")}</Badge>
                          ) : (
                            <Badge className="bg-slate-200 text-slate-600 text-xs px-1.5 py-0">{t("common.offline")}</Badge>
                          )}
                          {d.total_alerts > 0 && (
                            <span className="text-xs text-slate-500">{d.total_alerts} {t("health.alarm_label")}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-8">{t("health.no_devices")}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DeviceHealthPage;
